import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { ReportFilters, defaultReportFilters, type ReportFilterState } from './report-filters';
import { exportStockStatus } from '@/lib/export-stock-reports';

const fmt = (n: number) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const qty = (row: { byType: Record<string, { qty: number }> }, ...types: string[]) =>
  types.reduce((s, t) => s + (row.byType[t]?.qty ?? 0), 0);

export function StockStatusReport() {
  const { t } = useTranslation('trading');
  const [filters, setFilters] = useState<ReportFilterState>(defaultReportFilters);

  const { data, isLoading } = trpc.stockReports.getStockStatusByPeriod.useQuery({
    dateFrom: new Date(filters.dateFrom),
    dateTo: new Date(filters.dateTo + 'T23:59:59'),
    warehouseId: filters.warehouseId || undefined,
    brandId: filters.brandId || undefined,
    search: filters.search || undefined,
  });

  const items = data?.items ?? [];
  const totals = data?.totals;

  return (
    <div className="space-y-4">
      <ReportFilters
        value={filters}
        onChange={setFilters}
        show={{ warehouse: true, brand: true, search: true }}
        onExport={() => totals && exportStockStatus(items, totals)}
        exporting={false}
        exportDisabled={!items.length}
      />

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-2 py-2.5 text-left font-medium">{t('reports.code', 'Kod')}</th>
              <th className="px-2 py-2.5 text-left font-medium">{t('reports.product', 'Ürün')}</th>
              <th className="px-2 py-2.5 text-left font-medium">{t('reports.brand', 'Marka')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.devir', 'Devir')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.purchase', 'Alış')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.sale', 'Satış')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.return', 'İade')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.currentStock', 'Mevcut')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.cost', 'Maliyet')} (TL)</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.salesValue', 'Satış Değeri')} (TL)</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.profit', 'Kâr/Zarar')} (TL)</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={11} className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></td></tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">{t('reports.noResults', 'Kayıt bulunamadı')}</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.productId} className="border-b last:border-0 hover:bg-muted/30">
                <td className="whitespace-nowrap px-2 py-2 font-mono text-xs">{r.productCode}</td>
                <td className="max-w-[240px] px-2 py-2"><span className="block truncate">{r.productName}</span></td>
                <td className="px-2 py-2">{r.brandName}</td>
                <td className="px-2 py-2 text-right tabular-nums">{qty(r, 'INITIAL_STOCK') || ''}</td>
                <td className="px-2 py-2 text-right tabular-nums">{qty(r, 'PURCHASE_RECEIPT', 'TRANSFER_IN') || ''}</td>
                <td className="px-2 py-2 text-right tabular-nums">{qty(r, 'SALES_ISSUE') || ''}</td>
                <td className="px-2 py-2 text-right tabular-nums">{qty(r, 'RETURN_FROM_CUSTOMER') || ''}</td>
                <td className="px-2 py-2 text-right tabular-nums font-medium">{r.currentQty}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(r.cogs)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(r.salesValue)}</td>
                <td className={`px-2 py-2 text-right tabular-nums ${r.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(r.grossProfit)}</td>
              </tr>
            ))}
          </tbody>
          {totals && items.length > 0 && (
            <tfoot>
              <tr className="border-t-2 bg-muted/40 font-semibold">
                <td className="px-2 py-2.5" colSpan={8}>{t('reports.grandTotal', 'GENEL TOPLAM')}</td>
                <td className="px-2 py-2.5 text-right tabular-nums">{fmt(totals.cogs)}</td>
                <td className="px-2 py-2.5 text-right tabular-nums">{fmt(totals.salesValue)}</td>
                <td className={`px-2 py-2.5 text-right tabular-nums ${totals.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(totals.grossProfit)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('reports.statusNote', 'Maliyet = satılan malların FIFO maliyeti · Satış Değeri = dönem içi fatura satırlarından')}
      </p>
    </div>
  );
}
